import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/nav/Logo";

/**
 * Auth + access guard. Reads the require prop to decide whether the user
 * is allowed to render the page or where to redirect them instead.
 *
 *  require="in-onboarding" → must be authed AND not yet onboarded.
 *                            Plan is NOT required (PR: delayed-paywall).
 *  require="onboarded"     → must have completed all 3 steps. Without
 *                            onboarding_step="done", redirect to /dashboard
 *                            where the hero CTA picks up the flow.
 *  require=undefined       → just authed. Used for /pricing, /settings,
 *                            /dashboard — pages that should be reachable
 *                            without a paid plan.
 *
 * PR: delayed-paywall removed the unconditional "!user.plan → /pricing"
 * redirect. Payment is now enforced server-side on AI-cost endpoints
 * (see require_active_plan in backend) and contextually in the UI
 * (Step 3 shows a paywall card before calling /strategy/generate).
 */
const ProtectedRoute = ({ children, require }) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div
                className="min-h-screen flex flex-col items-center justify-center gap-5 bg-background"
                data-testid="auth-loading"
            >
                <div className="opacity-80">
                    <Logo />
                </div>
                <div className="relative">
                    <div
                        className="w-8 h-8 rounded-full border-2 border-border border-t-primary animate-spin"
                        aria-hidden="true"
                    />
                </div>
                <div className="font-mono text-xs text-faint-c tracking-wide">Завантаження…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

    // Onboarded pages need a fully-set-up profile + generated strategy.
    // No-plan users without a strategy land back on /dashboard which shows
    // the "Start setup" CTA.
    if (require === "onboarded" && user.onboarding_step !== "done") {
        return <Navigate to="/dashboard" replace />;
    }

    // In-onboarding pages should not be shown to users who already finished.
    // Plan is intentionally not checked — paywall is on the action button
    // inside step 3, not on the route itself.
    if (require === "in-onboarding" && user.onboarding_step === "done") {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

export default ProtectedRoute;
